/** Navigation menu tree — shared between dashboard, builder, and region instance. */

import type { MegaMenuRuntimeContent } from "../mega-menu";
import type {
	GroupMegaMenuHeaderRuntimePayload,
	NavIconPosition,
} from "./group-mega-menu-header";

export {
	buildMenuTreeFromFlat,
	compareMenuMegaGroups,
	sortMenuMegaGroups,
} from "./build-tree";
export { remapMegaMenuKeyedByTabs } from "./remap-mega-menu-keyed";
export { remapMegaMenuNavIcons } from "./remap-mega-menu-nav-icons";

export type MenuItemKind = "link" | "mega_tab" | "mega_group" | "mega_link";
export type MenuDropdownStyle = "none" | "mega";

export interface MenuItemFlat {
	columnIndex: number | null;
	/** Optional short blurb (mega links; ignored for other kinds at runtime). */
	description?: string | null;
	dropdownStyle: MenuDropdownStyle | null;
	href: string;
	id: string;
	itemKind: MenuItemKind;
	label: string;
	megaMenuId?: string | null;
	menuId?: string;
	openInNewTab: boolean;
	parentId: string | null;
	sortOrder: number;
}

export interface MenuMegaLinkNode {
	/** Optional one-line description under the link label. */
	description?: string | null;
	href: string;
	id: string;
	label: string;
	openInNewTab: boolean;
	sortOrder: number;
}

export interface MenuMegaGroupNode {
	columnIndex: number;
	/**
	 * Optional category URL. Empty / `#` in storage means plain label (no link).
	 */
	href?: string;
	id: string;
	label: string;
	links: MenuMegaLinkNode[];
	sortOrder: number;
}

export interface MenuMegaTabNode {
	/** Content columns in the mega panel (2–4). Stored as `columnIndex` on the mega_tab row. */
	columnCount: 2 | 3 | 4;
	dropdownStyle: "mega";
	groups: MenuMegaGroupNode[];
	href: string;
	id: string;
	itemKind: "mega_tab";
	label: string;
	megaMenuContent?: MegaMenuRuntimeContent | null;
	megaMenuId?: string | null;
	openInNewTab: boolean;
	sortOrder: number;
}

export interface MenuLinkNode {
	dropdownStyle: "none";
	href: string;
	id: string;
	itemKind: "link";
	label: string;
	openInNewTab: boolean;
	sortOrder: number;
}

export type MenuRootNode = MenuLinkNode | MenuMegaTabNode;

export interface MenuTree {
	id: string;
	name: string;
	roots: MenuRootNode[];
}

/** Featured area content: structured card (default) or editor HTML. */
export type MegaMenuHeaderPromoContentMode = "card" | "html";

/** Promo / featured box on `mega_menu_header` (keyed by mega tab item id). */
export interface MegaMenuHeaderPromo {
	/**
	 * `card` (default) — tag / title / gradient / href.
	 * `html` — custom HTML in the featured slot (`html` field).
	 */
	contentMode?: MegaMenuHeaderPromoContentMode;
	enabled: boolean;
	gradient: string;
	href: string;
	/** Used when `contentMode` is `"html"` (editor-authored markup). */
	html?: string;
	tag: string;
	title: string;
}

/** Horizontal pill row under mega panel columns (keyed by mega tab item id). */
export interface MegaMenuHeaderFooterLink {
	href: string;
	id: string;
	label: string;
}

export interface MegaMenuHeaderFooterRow {
	enabled: boolean;
	/** Section label above the pills (e.g. "By industry"). */
	label: string;
	links: MegaMenuHeaderFooterLink[];
}

export interface MegaMenuHeaderLocale {
	code: string;
	flag: string;
	/**
	 * Optional public URL or path for this locale/region (e.g. `/uk` or
	 * `https://example.com/uk`). Used to disable the current option and navigate.
	 */
	href?: string;
	label: string;
}

export interface MegaMenuHeaderActionLink {
	enabled: boolean;
	href: string;
	label: string;
}

export interface MegaMenuHeaderLanguageSwitcher {
	defaultCode: string;
	enabled: boolean;
	locales: MegaMenuHeaderLocale[];
}

export interface MegaMenuHeaderDesignTokens {
	/** Active audience segment background. Default `#e2a23b`. */
	audienceToggleActiveBackground?: string;
	/** Active audience segment text. Default `#3a2a08`. */
	audienceToggleActiveTextColor?: string;
	/** Audience toggle border (Individuals / Businesses). Default `#0c3b3c`. */
	audienceToggleBorderColor?: string;
	/** Inactive audience segment background. Default `#fff`. */
	audienceToggleInactiveBackground?: string;
	/** Inactive audience segment hover background. Default `#f3f7f6`. */
	audienceToggleInactiveHoverBackground?: string;
	/** Inactive audience segment text. Default `#16504f`. */
	audienceToggleInactiveTextColor?: string;
	bubbleBackground: string;
	/** Mega panel category / group titles. Default `#737373`. */
	categoryColor?: string;
	/** Category title hover (when linked). Default `#404040`. */
	categoryHoverColor?: string;
	/**
	 * When true (default), mega panel link columns use max width and are centered
	 * instead of stretching full bubble width.
	 */
	centerColumns?: boolean;
	/** Max width of each link column (px). Promo may use ~1.3× this. Default 240. */
	columnMaxWidthPx?: number;
	/** Get Started (CTA) background. Default `#0a0a0a`. */
	ctaBackground?: string;
	/** Get Started hover swipe accent. Default `#404040`. */
	ctaHoverBackground?: string;
	/** Get Started (CTA) label. Default `#fff`. */
	ctaTextColor?: string;
	/** Horizontal footer-row label (e.g. “By industry”). Default `rgba(0, 0, 0, 0.45)`. */
	footerLabelColor?: string;
	/** Horizontal footer pill background. Default `rgba(0, 0, 0, 0.04)`. */
	footerPillBackground?: string;
	/** Horizontal footer pill border. Default `rgba(0, 0, 0, 0.08)`. */
	footerPillBorderColor?: string;
	/** Horizontal footer pill hover background. Default `rgba(0, 0, 0, 0.07)`. */
	footerPillHoverBackground?: string;
	/** Horizontal footer pill hover text. Falls back to `footerPillTextColor`. */
	footerPillHoverTextColor?: string;
	/** Horizontal footer pill text. Falls back to `linkColor` / `textColor`. */
	footerPillTextColor?: string;
	/** Mega panel link labels. Falls back to `textColor`. */
	linkColor?: string;
	/** Mega panel link short description under the label. Default `rgba(0, 0, 0, 0.45)`. */
	linkDescriptionColor?: string;
	/** Mega panel link hover wash. Unset stays transparent. */
	linkHoverBackground?: string;
	/** Mega panel link hover. Default `#555`. */
	linkHoverColor?: string;
	/** Nav label when the sliding pill highlights the item. Default `#fff`. */
	navActiveTextColor?: string;
	/**
	 * Nav labels that are not on the pill while a mega/nav hover is open.
	 * Falls back to `textColor`.
	 */
	navInactiveOpenTextColor?: string;
	/**
	 * Max width (px) of the top header bar cluster (logo + nav + CTAs) and the mega
	 * panel inner content (columns + promo + footer pills). Outer bubble/panel stay
	 * full width; both inner rows are capped and centered.
	 * Unset or 0 = no constraint (backward compatible).
	 */
	panelContentMaxWidthPx?: number;
	pillColor: string;
	/** Unhighlighted nav / contact text (menu closed / idle). */
	textColor: string;
}

export type MegaMenuStickyMode = "always" | "smart";

export interface MegaMenuHeaderRuntimePayload {
	closeDelayMs: number;
	contact: MegaMenuHeaderActionLink;
	cta: MegaMenuHeaderActionLink;
	design: MegaMenuHeaderDesignTokens;
	/** Horizontal pill rows keyed by mega tab id. */
	footerRows: Record<string, MegaMenuHeaderFooterRow>;
	languageSwitcher: MegaMenuHeaderLanguageSwitcher;
	logoAlt: string;
	logoHeight?: number;
	/** Display height (px) of the logo in the header bar. Unset = default (36). */
	logoHeightPx?: number;
	logoHref: string;
	logoMediaId: string;
	/**
	 * Resolved secondary/open logo URL (injected from branding at publish / preview).
	 * When set and any top nav item is hovered, runtime swaps from `logoUrl`.
	 */
	logoOpenUrl?: string;
	/**
	 * Which Theme Builder branding logo to show while any top nav item is hovered.
	 * `secondary` uses branding secondary (inverse) logo when available.
	 */
	logoOpenVariant?: "primary" | "secondary";
	/** `sizes` hint paired with `logoSrcSet`. */
	logoSizes?: string;
	/** Display preset — sets height and caps delivered raster width. */
	logoSizeVariant?: "compact" | "default" | "large";
	/** Responsive srcset for branding logo (injected at publish). */
	logoSrcSet?: string;
	logoUrl?: string;
	logoWidth?: number;
	menuId: string;
	menuName?: string;
	promos: Record<string, MegaMenuHeaderPromo>;
	roots: MenuRootNode[];
	/**
	 * When true, the whole header (bar + mega panel) uses `position: sticky`.
	 * Existing payloads without this field stay unpinned.
	 */
	sticky?: boolean;
	/** `smart` hides the bar while scrolling down and reveals it on scroll up. */
	stickyMode?: MegaMenuStickyMode;
}

/**
 * Design tokens for `classic_header` — v1 Classic Header layout, modeled on
 * `docs/header-library/01-classic` (logo left, nav beside it, actions right,
 * compact anchored dropdowns). Maps to the header-library's `--hdr-*` variable
 * contract: `background` → `--hdr-h-bg`/`--hdr-panel-bg`, `textColor` → `--hdr-ink`,
 * `accentColor` → `--hdr-accent`/`--hdr-cta-bg`, `ctaTextColor` → `--hdr-cta-color`.
 */
export interface ClassicHeaderDesignTokens {
	accentColor: string;
	background: string;
	ctaTextColor: string;
	textColor: string;
}

export interface ClassicHeaderRuntimePayload {
	cta: MegaMenuHeaderActionLink;
	design: ClassicHeaderDesignTokens;
	logoAlt: string;
	logoHref: string;
	logoMediaId: string;
	logoUrl?: string;
	menuId: string;
	menuName?: string;
	roots: MenuRootNode[];
}

/**
 * `classic_header` default design — matches `docs/header-library/shared/header.css`'s
 * `:root` defaults: `--hdr-h-bg` (white), `--hdr-ink-2` (nav/body text),
 * `--hdr-accent-600` (amber accent + `--hdr-cta-bg`), `--hdr-primary-900` (`--hdr-cta-color`).
 */
export const DEFAULT_CLASSIC_HEADER_DESIGN: ClassicHeaderDesignTokens = {
	background: "#FFFFFF",
	textColor: "#33413F",
	accentColor: "#E2A23B",
	ctaTextColor: "#0A2F30",
};

function pickNonEmptyString(value: unknown, fallback: string): string {
	return typeof value === "string" && value.trim() ? value : fallback;
}

/**
 * Fills in any missing/empty `classic_header` design token with the
 * header-library default — old content saved before `accentColor`/`ctaTextColor`
 * existed (or a hand-edited/corrupted payload) must still render a complete,
 * themeable header instead of leaving CSS custom properties unset.
 */
export function mergeClassicHeaderDesign(
	design: Partial<ClassicHeaderDesignTokens> | null | undefined
): ClassicHeaderDesignTokens {
	return {
		background: pickNonEmptyString(
			design?.background,
			DEFAULT_CLASSIC_HEADER_DESIGN.background
		),
		textColor: pickNonEmptyString(
			design?.textColor,
			DEFAULT_CLASSIC_HEADER_DESIGN.textColor
		),
		accentColor: pickNonEmptyString(
			design?.accentColor,
			DEFAULT_CLASSIC_HEADER_DESIGN.accentColor
		),
		ctaTextColor: pickNonEmptyString(
			design?.ctaTextColor,
			DEFAULT_CLASSIC_HEADER_DESIGN.ctaTextColor
		),
	};
}

/** One audience slot for `audience_mega_menu_header` (exactly two in v1). */
export interface AudienceMegaMenuSlotPayload {
	footerRows: Record<string, MegaMenuHeaderFooterRow>;
	hubUrl: string;
	id: string;
	label: string;
	menuId: string;
	menuName?: string;
	promos: Record<string, MegaMenuHeaderPromo>;
	roots: MenuRootNode[];
}

/** Runtime payload for dual-audience mega menu header. */
export interface AudienceMegaMenuHeaderRuntimePayload {
	audiences: [AudienceMegaMenuSlotPayload, AudienceMegaMenuSlotPayload];
	closeDelayMs: number;
	contact: MegaMenuHeaderActionLink;
	cta: MegaMenuHeaderActionLink;
	defaultAudienceId: string;
	design: MegaMenuHeaderDesignTokens;
	languageSwitcher: MegaMenuHeaderLanguageSwitcher;
	logoAlt: string;
	logoHeight?: number;
	/** Display height (px) of the logo in the header bar. Unset = default (36). */
	logoHeightPx?: number;
	logoHref: string;
	logoMediaId: string;
	/**
	 * Resolved secondary/open logo URL (injected from branding at publish / preview).
	 */
	logoOpenUrl?: string;
	/**
	 * Which Theme Builder branding logo to show while any top nav item is hovered.
	 * `secondary` uses branding secondary (inverse) logo when available.
	 */
	logoOpenVariant?: "primary" | "secondary";
	/** `sizes` hint paired with `logoSrcSet`. */
	logoSizes?: string;
	/** Display preset — sets height and caps delivered raster width. */
	logoSizeVariant?: "compact" | "default" | "large";
	/** Responsive srcset for branding logo (injected at publish). */
	logoSrcSet?: string;
	logoUrl?: string;
	logoWidth?: number;
	navigateOnToggle: boolean;
	persistPreference: boolean;
	sticky?: boolean;
	stickyMode?: MegaMenuStickyMode;
	tipLabel: string;
}

/** Inline-persisted Iconify icon (sanitized SVG baked in at pick time). */
export interface AdvancedMegaMenuNavIcon {
	/** Picker hex; SVG fill is usually baked in — used when markup uses currentColor. */
	color?: string;
	iconId: string;
	/** Per-item override; falls back to replica `group.behavior.navIconPosition`. */
	iconPosition?: NavIconPosition;
	/** Picker size in px; applied on the icon box at render. */
	sizePx?: number;
	svgMarkup: string;
}

/** Contact / CTA action link with optional left+right icon slots (both may be set at once). */
export interface AdvancedMegaMenuHeaderActionLink
	extends MegaMenuHeaderActionLink {
	iconLeftAriaLabel?: string;
	iconLeftIconId?: string;
	iconLeftSvgMarkup?: string;
	iconRightAriaLabel?: string;
	iconRightIconId?: string;
	iconRightSvgMarkup?: string;
}

export interface AdvancedMegaMenuPromoCta {
	href: string;
	id: string;
	label: string;
	style?: "primary" | "secondary" | "link";
}

/** One promo card (`advanced_mega_menu_header` supports a list per tab, not a single card). */
export interface AdvancedMegaMenuPromoCard {
	contentMode?: MegaMenuHeaderPromoContentMode;
	ctas: AdvancedMegaMenuPromoCta[];
	gradient: string;
	/** Whole-card click-through fallback when no `ctas` are set. */
	href: string;
	html?: string;
	id: string;
	imageAlt?: string;
	/** File Manager asset id (usage tracking). */
	imageMediaId?: string;
	/** Resolved public URL — what actually renders. */
	imageUrl?: string;
	tag: string;
	title: string;
}

export interface AdvancedMegaMenuHeaderDesignTokens
	extends MegaMenuHeaderDesignTokens {
	/** Per-nav-item icon color. Falls back to `linkColor` / `textColor`. */
	navIconColor?: string;
	/** Promo card CTA button background. Default `#0a0a0a`. */
	promoCtaBackground?: string;
	/** Promo card CTA button hover background. Default `#404040`. */
	promoCtaHoverBackground?: string;
	/** Promo card CTA button label. Default `#fff`. */
	promoCtaTextColor?: string;
	/** Promo image overlay tint (drawn above the image, below the tag/title). */
	promoImageOverlayColor?: string;
}

export interface AdvancedMegaMenuHeaderRuntimePayload {
	/** Replica dense header; omit / `classic` keeps the pill advanced header. */
	chromeVariant?: "classic" | "replica";
	closeDelayMs: number;
	contact: AdvancedMegaMenuHeaderActionLink;
	cta: AdvancedMegaMenuHeaderActionLink;
	design: AdvancedMegaMenuHeaderDesignTokens;
	/** Horizontal pill rows keyed by mega tab id. */
	footerRows: Record<string, MegaMenuHeaderFooterRow>;
	group?: GroupMegaMenuHeaderRuntimePayload;
	languageSwitcher: MegaMenuHeaderLanguageSwitcher;
	logoAlt: string;
	logoHeight?: number;
	logoHeightPx?: number;
	logoHref: string;
	logoMediaId: string;
	logoOpenUrl?: string;
	logoOpenVariant?: "primary" | "secondary";
	/** `sizes` hint paired with `logoSrcSet`. */
	logoSizes?: string;
	/** Display preset — sets height and caps delivered raster width. */
	logoSizeVariant?: "compact" | "default" | "large";
	/** Responsive srcset for branding logo (injected at publish). */
	logoSrcSet?: string;
	logoUrl?: string;
	logoWidth?: number;
	menuId: string;
	menuName?: string;
	/** Icons keyed by menu item id (root link, mega tab, group, or group link). */
	navIcons: Record<string, AdvancedMegaMenuNavIcon>;
	/** Promo cards keyed by mega tab id — a list, not a single card. */
	promos: Record<string, AdvancedMegaMenuPromoCard[]>;
	roots: MenuRootNode[];
	sticky?: boolean;
	stickyMode?: MegaMenuStickyMode;
}

export type {
	GroupHeaderActions,
	GroupHeaderBarLayout,
	GroupHeaderBehavior,
	GroupHeaderBrand,
	GroupHeaderBreakpoints,
	GroupHeaderCtaLink,
	GroupHeaderDeadline,
	GroupHeaderDeadlinesWidget,
	GroupHeaderDenseGroup,
	GroupHeaderDenseLink,
	GroupHeaderDesignTokens,
	GroupHeaderDrawerBodyLayout,
	GroupHeaderDrawerSide,
	GroupHeaderFeaturedCard,
	GroupHeaderFeaturedContentMode,
	GroupHeaderFooterNavLink,
	GroupHeaderGroupMoreLink,
	GroupHeaderLinkBadge,
	GroupHeaderLinkBadgeTone,
	GroupHeaderLogoSwitcher,
	GroupHeaderMegaPanelLayout,
	GroupHeaderMobileDrawer,
	GroupHeaderMobileStyle,
	GroupHeaderPanelConfig,
	GroupHeaderPanelFooter,
	GroupHeaderProductCard,
	GroupHeaderProductCardLink,
	GroupHeaderProductMarkKind,
	GroupHeaderRegion,
	GroupHeaderRegionSwitcher,
	GroupHeaderSearch,
	GroupHeaderSearchStyle,
	GroupHeaderStatusPill,
	GroupHeaderStickyMode,
	GroupHeaderSubcategoryMode,
	GroupHeaderTicker,
	GroupHeaderTypography,
	GroupHeaderUtility,
	GroupHeaderUtilityLink,
	GroupMegaMenuHeaderElementType,
	GroupMegaMenuHeaderPayloadPatch,
	GroupMegaMenuHeaderRuntimePayload,
	NavIconPosition,
} from "./group-mega-menu-header";
export {
	authoredGroupHeaderColor,
	DEFAULT_GROUP_HEADER_BEHAVIOR,
	DEFAULT_GROUP_HEADER_BREAKPOINTS,
	DEFAULT_GROUP_HEADER_DESIGN,
	DEFAULT_GROUP_HEADER_MOBILE,
	DEFAULT_GROUP_HEADER_SEARCH,
	DEFAULT_GROUP_HEADER_TYPOGRAPHY,
	EMPTY_GROUP_MEGA_MENU_HEADER_PAYLOAD,
	GROUP_HEADER_LAYOUT_ALIASES,
	GROUP_HEADER_THEME_TOKEN_DEFAULTS,
	isInheritedGroupHeaderColor,
	LEGACY_GROUP_HEADER_HEX,
	mergeGroupHeaderBehavior,
	mergeGroupHeaderBreakpoints,
	mergeGroupHeaderDesign,
	mergeGroupHeaderMobile,
	mergeGroupHeaderPanelConfig,
	mergeGroupHeaderPanels,
	mergeGroupHeaderSearch,
	mergeGroupHeaderTypography,
	mergeGroupMegaMenuHeaderPayload,
	parseGroupHeaderMegaPanelLayout,
	parseGroupHeaderProductMarkKind,
	resolveGroupHeaderColor,
	resolveGroupHeaderDesign,
} from "./group-mega-menu-header";
